import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Slider from '../../../01_atoms/Slider';
import SliderNavigationIcon from '../../../../public/static/icons/arrows/carousel-button-outline.svg';
import ActivismBlockInfo from '../../../02_molecules/ActivismBlockInfo';
import Link, { propTypes as linkPropTypes } from '../../../01_atoms/Link';
import Button from '../../../01_atoms/Button';
import TextButton from '../../../01_atoms/TextButton';
import DefaultPicture, { propTypes as picturePropTypes } from '../../../01_atoms/DefaultPicture';
import {
  behaviorSettingsProps,
  generateClassNameByBehaviorSettings,
} from '../../../../utils/behaviorSettings';
import { pushSliderHasChanged as gtmPushSliderHasChanged } from '../../../../utils/gtm';

import styles from './index.module.scss';

const defaultSliderSettings = {
  dots: false,
  infinite: false,
  lazyLoad: false,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: false,
  initialSlide: 0,
};

const BBActivismStoryCarousel = ({
  title = '',
  subtitle = '',
  items,
  behaviorSettings = null,
  uuid = null,
}) => {
  const [slideIndex, setSlideIndex] = useState(0);
  const sliderRef = useRef(null);
  const onNextClick = () => sliderRef.current && sliderRef.current.slickNext();
  const onPrevClick = () => sliderRef.current && sliderRef.current.slickPrev();

  const sliderSettings = defaultSliderSettings;
  sliderSettings.afterChange = (index) => {
    const newIndex = Math.round(index);

    setSlideIndex(newIndex);

    gtmPushSliderHasChanged({
      uuid,
      type: 'activism_story_carousel',
      current_item: newIndex,
    });
  };

  const classes = [
    'bb',
    styles['bb-story-carousel'],
    generateClassNameByBehaviorSettings(behaviorSettings),
  ];

  return (
    <div className={classes.join(' ')} id={uuid}>
      <ActivismBlockInfo title={title} subtitle={subtitle} />
      <div className="container">
        <div className={styles['bb-story-carousel__slider']}>
          <Slider {...sliderSettings} ref={sliderRef}>
            {items.map((item, i) => (
              <div
                className={styles['bb-story-carousel__slide']}
                key={`activism-story-carousel-slide-${i}`}
              >
                {/* Image lazy loading is disabled due to some bug in react-slick & Chrome.
                  See https://github.com/akiran/react-slick/pull/1954 and https://www.pivotaltracker.com/story/show/178381170 */}
                <DefaultPicture {...item.image} />

                <div className={styles['bb-story-carousel__gradient']} />
                <div className={styles['bb-story-carousel__overlay']}>
                  <div className={styles['bb-story-carousel__text']}>
                    {item.title && (
                      <h3 className={styles['bb-story-carousel__title']}>{item.title}</h3>
                    )}
                    {item.description && (
                      <p className={styles['bb-story-carousel__description']}>{item.description}</p>
                    )}

                    {item.cta && item.cta.nextLink && (
                      <Link {...item.cta.nextLink}>
                        <TextButton
                          type="light"
                          href={item.cta.nextLink.url}
                          tag="a"
                          className={styles['bb-story-carousel__text-button']}
                          withArrow
                          // Ignore links for all slides apart from active slide to have good keyboard navigation.
                          tabIndex={slideIndex === i ? '0' : '-1'}
                        >
                          {item.cta.label}
                        </TextButton>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </Slider>

          <div className={styles['bb-story-carousel__arrows']}>
            <SliderNavigationIcon
              tabIndex={slideIndex === 0 ? '-1' : '0'}
              // TODO remove bb-activism-carousel--navigation class name when custom analytics event is added.
              className={`bb-activism-carousel--navigation ${styles['bb-story-carousel__arrow']} ${
                styles['bb-story-carousel__arrow--prev']
              } ${slideIndex === 0 ? styles['bb-story-carousel__arrow--inactive'] : ''}`}
              onClick={onPrevClick}
              onKeyPress={(e) => e.key === 'Enter' && onPrevClick()}
            />
            <div className={styles['bb-story-carousel__info']}>
              {slideIndex + 1}/{items.length}
            </div>
            <SliderNavigationIcon
              tabIndex={slideIndex === items.length - 1 ? '-1' : '0'}
              // TODO remove bb-activism-carousel--navigation class name when custom analytics event is added.
              className={`bb-activism-carousel--navigation ${styles['bb-story-carousel__arrow']} ${
                styles['bb-story-carousel__arrow--next']
              } ${
                slideIndex === items.length - 1 ? styles['bb-story-carousel__arrow--inactive'] : ''
              }`}
              onClick={onNextClick}
              onKeyPress={(e) => e.key === 'Enter' && onNextClick()}
            />
            <Button
              tabIndex={slideIndex === items.length - 1 ? '-1' : '0'}
              type="activism"
              onClick={onNextClick}
              disabled={slideIndex === items.length - 1}
              // TODO remove bb-activism-carousel--navigation class name when custom analytics event is added.
              className={`bb-activism-carousel--navigation ${
                styles['bb-story-carousel__next-button']
              } ${
                slideIndex === items.length - 1
                  ? styles['bb-story-carousel__next-button--inactive']
                  : ''
              }`}
              withArrow
            >
              {items[slideIndex].nextButtonLabel
                ? items[slideIndex].nextButtonLabel
                : 'Continue the story'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

BBActivismStoryCarousel.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.shape(picturePropTypes).isRequired,
      nextButtonLabel: PropTypes.string,
      cta: PropTypes.shape({
        label: PropTypes.string.isRequired,
        nextLink: PropTypes.shape(linkPropTypes).isRequired,
      }),
    }),
  ).isRequired,
  behaviorSettings: behaviorSettingsProps,
  uuid: PropTypes.string,
};

export default BBActivismStoryCarousel;
